import { useState } from "react";
import { db } from "../firebase/config"; // abc-dashboard's own DB — same draft data SetupWizard.jsx writes
import { doc, getDoc, collection, getDocs } from "firebase/firestore";

// ── Resume Setup screen ──────────────────────────────────────────
// Entry point for a non-technical user who closed the browser mid-setup.
// Just takes a Society ID and hands off to fetchDraftState() below —
// SetupWizard.jsx owns turning the result into React state, since that's
// where every other screen's state already lives.
export function ResumeSetup({ onResume, onBack }) {
  const [societyId, setSocietyId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleResume() {
    const id = societyId.trim().toUpperCase();
    if (!id) { setError("Enter your Setup ID."); return; }
    setLoading(true);
    setError("");
    try {
      const draft = await fetchDraftState(id);
      if (!draft) { setError("No setup found with that ID. Check it and try again."); return; }
      if (draft.status !== "draft") { setError("This setup has already been completed — please log in instead."); return; }
      onResume(draft);
    } catch (err) {
      console.error("Resume fetch failed:", err);
      setError("Couldn't check that ID: " + (err.message || "Unknown error. Check your connection."));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      maxWidth: "420px", margin: "0 auto", background: "#181820", border: "1px solid #2e2e40",
      borderRadius: "16px", padding: "28px",
    }}>
      <div style={{ fontSize: "18px", fontWeight: "700", color: "#e8e8f0", marginBottom: "6px" }}>Resume Setup</div>
      <div style={{ fontSize: "13px", color: "#8888a8", marginBottom: "18px" }}>
        Enter the Setup ID you saved earlier to pick up where you left off.
      </div>

      {error && (
        <div style={{ background: "#3a1f1f", border: "1px solid #6b2c2c", borderRadius: "8px", padding: "10px 14px", fontSize: "13px", color: "#ff9d9d", marginBottom: "16px" }}>
          ⚠ {error}
        </div>
      )}

      <div style={{ marginBottom: "18px" }}>
        <label style={{ display: "block", fontSize: "12px", color: "#8888a8", marginBottom: "6px" }}>Setup ID</label>
        <input
          style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px", borderRadius: "10px", border: "1px solid #2e2e40", background: "#0f0f14", color: "#e8e8f0", fontSize: "15px" }}
          value={societyId}
          onChange={e => setSocietyId(e.target.value)}
          placeholder="e.g. GMH2381"
          disabled={loading}
        />
      </div>

      <div style={{ display: "flex", gap: "10px" }}>
        <button
          style={{ flex: 1, padding: "12px", borderRadius: "10px", border: "1px solid #2e2e40", background: "transparent", color: "#8888a8", fontSize: "14px", cursor: loading ? "not-allowed" : "pointer" }}
          onClick={onBack} disabled={loading}
        >← Back</button>
        <button
          style={{ flex: 2, padding: "12px", borderRadius: "10px", border: "none", background: "#6c63ff", color: "#fff", fontSize: "14px", fontWeight: "600", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}
          onClick={handleResume} disabled={loading}
        >{loading ? "Checking…" : "Resume →"}</button>
      </div>
    </div>
  );
}

// ── fetchDraftState ───────────────────────────────────────────────
// Reads societies/{id}, its settings/init doc, and its flats subcollection,
// and returns a plain object SetupWizard.jsx uses to hydrate every piece
// of its own state. Returns null if the society doesn't exist.
//
// Also computes `resumeStep` — the furthest step with no saved data yet,
// i.e. exactly where the person left off, since each wizard step only
// writes to settings/init once its own Next button is clicked.
export async function fetchDraftState(societyId) {
  const societySnap = await getDoc(doc(db, "societies", societyId));
  if (!societySnap.exists()) return null;

  const settingsSnap = await getDoc(doc(db, "societies", societyId, "settings", "init"));
  const settings = settingsSnap.exists() ? settingsSnap.data() : {};

  const flatsSnap = await getDocs(collection(db, "societies", societyId, "flats"));
  const flats = flatsSnap.docs
    .map(d => d.data())
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    .map(f => ({
      flatNo: f.flatNo || "", block: f.block || "", floor: f.floor || "",
      legalOwnerName: f.legalOwnerName || "", representativeName: f.representativeName || "",
      flatType: f.flatType || "", sqFt: f.sqFt || "", doorNo: f.doorNo || "",
      propertyTaxNo: f.propertyTaxNo || "",
      electricityServiceNo: f.electricityServiceNo || "", electricityUscNo: f.electricityUscNo || "",
      mobile1: f.mobile1 || "", mobile2: f.mobile2 || "", emailId: f.emailId || "",
      status: f.status || "Occupied", mcRole: f.mcRole || "", openingArrearsMonths: f.openingArrearsMonths ?? "", rate: f.maintenanceRate || 0,
    }));

  let resumeStep = 0;
  if (settings.totalFlats) resumeStep = 1;
  if (settings.totalFlats && settings.vacantMethod) resumeStep = 2;
  if (settings.reconciliationMethod) resumeStep = settings.reconciliationMethod === "manual" ? 4 : 3;
  if (settings.reconciliationMethod === "auto" && settings.bankSync) resumeStep = 4;
  if (flats.length > 0) resumeStep = 5;
  if (settings.pricing) resumeStep = 6;

  return {
    status: societySnap.data().status,
    societyId,
    society: {
      societyName: settings.societyName || societySnap.data().name || "",
      location: settings.location || "", city: settings.city || "",
      pincode: settings.pincode || "", societyEmail: settings.societyEmail || "",
      totalFlats: settings.totalFlats ? String(settings.totalFlats) : "",
      occupiedRate: settings.maintenanceAmt ? String(settings.maintenanceAmt) : "",
      vacantMethod: settings.vacantMethod || "fixed",
      vacantRate: settings.vacantAmt ? String(settings.vacantAmt) : "",
      vacantPct: settings.vacantPct ? String(settings.vacantPct) : "",
      dueCutoffDay: settings.dueCutoffDay ? String(settings.dueCutoffDay) : "10",
      waterDeposit: settings.waterDeposit ? String(settings.waterDeposit) : "",
      corpusFund: settings.corpusFund ? String(settings.corpusFund) : "",
      waterApprovalFlow: settings.waterApprovalFlow || "two-step",
    },
    mcMemberCount: settings.mcMemberCount || "1",
    featuresEnabled: settings.features || null, // null → SetupWizard keeps its own default
    waterBillingMode: settings.waterBillingMethod === "equal" ? "equal" : "meter",
    reconciliationMethod: settings.reconciliationMethod || null,
    bankSync: settings.bankSync
      ? {
          provider: settings.bankSync.provider || "gmail",
          host: settings.bankSync.host || "",
          email: settings.bankSync.email || "",
          appPassword: "", // the password is never read back from the draft
          mailboxFolder: settings.bankSync.mailboxFolder || "",
          passwordSaved: settings.bankSync.passwordSaved === true,
        }
      : null,
    flats,
    pricing: settings.pricing || null, // { type: "tier", label, priceMonthly } or { type: "custom", message }
    resumeStep,
  };
}
